import { Button, ProductCard } from '@/components/atoms';
import { CONSTANTS, HELPERS } from '@/utils';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdClose, MdFilterList } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { PageBanner } from '@/components/organisms';
import useReady from '@/hooks/useReady';
import imgEmptyEvents from '@/images/no-events.png';
import { ACTION_CATALOG } from '@/redux/catalog/catalog.action';
import { ACTION_EVENT } from '@/redux/event/event.action';
import { SERVICE_EVENT } from '@/services';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import CatalogFilter from './components/CatalogFilter';
import SearchBar from './components/SearchBar';
import SmoothTransition from './components/SmoothWrapper';
import CatalogItemSkeletons from './components/skeletons/CatalogItemSkeletons';
import { bigBackground, smallBackground } from './components/assets/background';

const EmptyStateComponent = ({
  isFetching,
  onReset,
  fullHeight = true,
  emptyMessage = 'Tidak ada acara yang cocok dengan pencarian Anda. Coba periksa ejaan atau gunakan kata lain',
  buttonText = 'Kembali Lihat Semua Acara',
  image = imgEmptyEvents,
  imageAlt = 'Empty Events',
}) => {
  return (
    <div
      className={`p-8 text-center bg-white rounded-lg shadow-sm flex flex-col items-center justify-center ${fullHeight ? 'min-h-[calc(100vh-335px)]' : ''
        }`}
    >
      {isFetching ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary mb-4" />
          <p className="text-gray-600 font-medium">Memuat acara...</p>
        </div>
      ) : (
        <>
          <img src={image} alt={imageAlt} className="w-40 h-44 mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-6 max-w-md">{emptyMessage}</h3>
          {onReset && (
            <Button
              variant="tertiary"
              onClick={onReset}
              className="px-6 py-2 text-white bg-tertiary-500 hover:bg-tertiary-dark transition-colors"
            >
              {buttonText}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

const useResponsiveBackground = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile ? smallBackground : bigBackground;
};

const EventCatalogue = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();
  // eslint-disable-next-line no-unused-vars
  const auth = useSelector((state) => state.auth.profile);

  // For URL effect to avoid loops
  const isFirstRender = useRef(true);

  // Get search query from Redux store
  const query = useSelector((state) => state.catalog.query);
  const preventScrollReset = (callback) => {
    // Simpan posisi scroll saat ini
    const scrollPosition = window.scrollY;

    // Jalankan callback
    callback();

    // Kembalikan posisi scroll setelah semua operasi selesai
    // Gunakan setTimeout dengan beberapa waktu berbeda untuk memastikan posisi scroll kembali
    setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    setTimeout(() => window.scrollTo(0, scrollPosition), 50);
    setTimeout(() => window.scrollTo(0, scrollPosition), 100);
  };
  // Get event categories from Redux store - safer approach
  const categories = useSelector((state) => state.event?.categories?.payload?.categories || []);
  const categoriesLoading = useSelector((state) => state.event?.categories?.loading || false);

  // Filter states
  const [eventType, setEventType] = useState('');
  const [eventKind, setEventKind] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [categoryId, setCategoryId] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [perPage, setPerPage] = useState(10);

  // State for smooth transitions
  const [previousData, setPreviousData] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { SIZES, VARIANTS } = Button;

  // Use memoized selectedCategories to prevent unnecessary re-renders
  const selectedCategories = useMemo(() => {
    if (!categoryId || categories.length === 0) return [];

    const selectedIds = categoryId.split(',');
    const result = [];

    selectedIds.forEach((id) => {
      // Check if it's a main category
      const mainCategory = categories.find((cat) => cat.id.toString() === id);
      if (mainCategory) {
        result.push(mainCategory);
        return;
      }

      // FIXED: Using array method instead of for...of loop
      categories.some((cat) => {
        if (cat.subcategories && cat.subcategories.length > 0) {
          const subcat = cat.subcategories.find((sub) => sub.id.toString() === id);
          if (subcat) {
            // Add parent info to the subcategory for display
            result.push({
              ...subcat,
              isSubcategory: true,
              parentTitle: cat.title,
            });
            return true; // Stop iteration once found
          }
        }
        return false;
      });
    });

    return result;
  }, [categoryId, categories]);

  // Track when filters are ready to trigger refetch
  const { ready, resetReady } = useReady(500, [query, priceMin, priceMax, categoryId, eventType, eventKind]);

  // Load categories only once when component mounts
  useEffect(() => {
    if (categories.length === 0 && !categoriesLoading) {
      // eslint-disable-next-line no-console
      console.log('Fetching event categories from EventCatalogue component');
      dispatch(ACTION_EVENT.getEventCategories());
    }
  }, [dispatch, categories.length, categoriesLoading]);
  const isEmptyQuery = query === '';
  const eventQueryKey = [
    'events',
    {
      query,
      min_price: priceMin,
      max_price: priceMax,
      category: categoryId,
      event_type: eventType,
      event_kind: eventKind,
      per_page: perPage,
    },
  ];
  // Fetch events list with filters and proper caching
  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: eventsData,
    isLoading,
    isFetching,
    refetch,
    // eslint-disable-next-line no-unused-vars
    isPreviousData,
  } = useInfiniteQuery({
    queryKey: eventQueryKey,
    queryFn: async ({ queryKey, pageParam = 1 }) => {
      const filters = queryKey[1] ? { ...queryKey[1], page: pageParam } : { page: pageParam };
      const apiCallFilters = { ...filters };
      if (filters.min_price || filters.max_price) {
        delete apiCallFilters.min_price;
        delete apiCallFilters.max_price;
      }
      const response = await SERVICE_EVENT.getEventsList(apiCallFilters);
      return response.payload?.data || [];
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.length === perPage) {
        return allPages?.length + 1;
      }
      return undefined;
    },
    enabled: ready,
    staleTime: 300000,
    cacheTime: 600000,
    keepPreviousData: true,
    placeholderData: previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const processedAndFilteredEventPages = useMemo(() => {
    if (!eventsData?.pages) {
      return [];
    }

    return eventsData.pages.map(pageOfEvents => {
      if (!Array.isArray(pageOfEvents)) return [];

      const eventsWithDisplayPrice = pageOfEvents.map(event => ({
        ...event,
        displayPrice: HELPERS.calculateEventDisplayPrice(event),
      }));

      if (priceMin || priceMax) {
        return eventsWithDisplayPrice.filter(event => {
          const dp = event.displayPrice;
          const meetsMin = priceMin ? dp >= Number(priceMin) : true;
          const meetsMax = priceMax ? dp <= Number(priceMax) : true;
          return meetsMin && meetsMax;
        });
      }
      return eventsWithDisplayPrice;
    });
  }, [eventsData, priceMin, priceMax]);

  useEffect(() => {
    let timer;
    if (isFetching && eventsData && !isTransitioning) {
      setPreviousData(eventsData);
      setIsTransitioning(true);
    }

    if (!isFetching && isTransitioning) {
      timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 150);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isFetching, eventsData, isTransitioning]);

  useEffect(() => {
    return () => {
      queryClient.cancelQueries(['events']);
    };
  }, [queryClient]);

  useEffect(() => {
    const fetchDefaultEvents = async () => {
      try {
        if (!eventsData || !eventsData.pages || eventsData.pages.length === 0) {
          await refetch();
        }
      } catch (error) {
        console.error('Failed to fetch default events:', error);
      }
    };

    fetchDefaultEvents();
  }, [eventsData, refetch]);

  const handleChangeQuery = (value) => {
    if (value === query) return;

    dispatch(ACTION_CATALOG.setSearchQuery(value));
    resetReady();
  };

  const handleResetFilter = (isExitCatalog = false) => {
    const hasChanges = query || eventType || eventKind || priceMin || priceMax || categoryId;
    if (!hasChanges) return;

    const batchUpdates = () => {
      handleChangeQuery('');
      setEventType('');
      setEventKind('');
      setPriceMin('');
      setPriceMax('');
      setCategoryId('');
    };
    history.replace(location.pathname);

    batchUpdates();

    queryClient.removeQueries(['events'], { exact: false });
    resetReady();
  };

  const handleChangeProductType = (typeValue) => {
    setEventType(typeValue);
  };

  const handleChangeEventKind = (kindValue) => {
    setEventKind(kindValue);
  };

  const handleRemoveFilterCategory = (cat) => {
    if (!categoryId) return;

    const listCategory = categoryId.split(',');
    const indexCat = listCategory.indexOf(cat.id.toString());
    if (indexCat !== -1) {
      listCategory.splice(indexCat, 1);
      setCategoryId(listCategory.join(','));
    }
  };

  useEffect(() => {
    if (ready) {
      preventScrollReset(() => {
        const queries = {};
        if (query) queries.query = query;
        if (priceMin) queries.min_price = priceMin;
        if (priceMax) queries.max_price = priceMax;
        if (categoryId) queries.category = categoryId;
        if (eventType) queries.event_type = eventType;
        if (eventKind) queries.event_kind = eventKind;

        const queryString = HELPERS.objectToQueryString(queries);
        const newPath = `${location.pathname}${queryString}`;

        if (location.pathname + location.search !== newPath) {
          history.replace(newPath);
          if (!query && location.search.includes('query=')) {
            queryClient.removeQueries(['events'], { exact: false });
          }
        }
      });
    }
  }, [ready, query, priceMin, priceMax, categoryId, eventType, eventKind]);

  // Parse URL parameters on component mount
  useEffect(() => {
    let params = {};

    if (location.search) {
      params = HELPERS.queryStringToObject(location.search);

      if (params.query) handleChangeQuery(decodeURIComponent(params.query));
      if (params.event_type) setEventType(params.event_type);
      if (params.event_kind) setEventKind(params.event_kind);
      if (params.min_price) setPriceMin(params.min_price);
      if (params.max_price) setPriceMax(params.max_price);
      if (params.category) setCategoryId(params.category);
    }
  }, [location.search]);

  // Send analytics data when events load
  useEffect(() => {
    if (eventsData?.pages?.length > 0 && eventsData.pages[0].length > 0) {
      const data = eventsData.pages[0];

      const gaPayload = {
        item_list_id: 'event_list',
        item_list_name: 'Event List',
        items: data.map((item) => ({
          item_id: item.uuid,
          item_name: item.title,
          price: item.ticket_price,
        })),
      };

      // eslint-disable-next-line no-console
      HELPERS.sendEventGA('view_item_list', gaPayload);
    }
  }, [eventsData]);

  // eslint-disable-next-line no-unused-vars
  const contentClasses = classNames({
    'transition-all duration-300 ease-in-out': true,
    'opacity-70': isFetching && !isLoading,
    'opacity-100': !isFetching || isLoading,
  });

  const [showCatalogFilterMobile, setShowCatalogFilterMobile] = useState(false);

  // Create a title based on filter selections
  const pageTitle = useMemo(() => {
    let title = t(CONSTANTS.PRODUCT_TYPE.EVENT.value);

    // Add category to title if selected
    if (selectedCategories.length === 1) {
      title = `${selectedCategories[0].title} ${title}`;
    }

    // Add search query to title if present
    if (query) {
      title = `${query} - ${title}`;
    }

    // Add type to title if selected
    if (eventType === 'online') {
      title = `${title} Online`;
    } else if (eventType === 'offline') {
      title = `${title} Offline`;
    } else if (eventType === 'hybrid') {
      title = `${title} Hybrid`;
    }

    return title;
  }, [t, query, selectedCategories, eventType]);

  // Create a description based on filters
  const pageDescription = useMemo(() => {
    let description = `${t('Temukan berbagai')} ${t(CONSTANTS.PRODUCT_TYPE.EVENT.value).toLowerCase()}`;

    if (selectedCategories.length > 0) {
      const categoryNames = selectedCategories.map((cat) => cat.title).join(', ');
      description += ` dalam kategori ${categoryNames}`;
    }

    if (eventType === 'online') {
      description += ' yang diselenggarakan secara online';
    } else if (eventType === 'offline') {
      description += ' yang diselenggarakan secara offline';
    } else if (eventType === 'hybrid') {
      description += ' yang diselenggarakan secara hybrid';
    }

    if (query) {
      description += ` untuk "${query}"`;
    }

    return description;
  }, [t, selectedCategories, eventType, query]);

  const ResponsiveBackground = useResponsiveBackground();

  return (
    <>
      <PageBanner
        title={t(CONSTANTS.PRODUCT_TYPE.EVENT.value)}
        backgroundComponent={ResponsiveBackground}
      />
      <div className="bg-gray-100 w-full py-4 lg:py-10">
        <div className="mx-auto w-fit lg:container space-y-6 gap-6 lg:flex lg:space-y-0 lg:space-x-5">
          <div className="flex-shrink-0 w-full lg:w-1/3 lg:max-w-[282px]">
            <CatalogFilter
              productType={eventType}
              eventKind={eventKind}
              priceMin={priceMin}
              priceMax={priceMax}
              categoryIds={categoryId}
              onReset={(isExitCatalog) => preventScrollReset(() => handleResetFilter(isExitCatalog))}
              onChangePriceMin={(value) => preventScrollReset(() => setPriceMin(value))}
              onChangePriceMax={(value) => preventScrollReset(() => setPriceMax(value))}
              onChangeProductType={(value) => preventScrollReset(() => handleChangeProductType(value))}
              onChangeEventKind={(value) => preventScrollReset(() => handleChangeEventKind(value))}
              onChangeCategory={(value) => preventScrollReset(() => setCategoryId(value))}
              showCatalogFilterMobile={showCatalogFilterMobile}
              setShowCatalogFilterMobile={setShowCatalogFilterMobile}
            />
          </div>

          <div className="lg:flex-1 w-full">
            <SmoothTransition isLoading={isLoading} isFetching={isFetching} className="h-full">
              {isLoading ? (
                <CatalogItemSkeletons />
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4 gap-3 sticky top-0 bg-gray-100 z-30 py-2 px-4 lg:px-0">
                    <div className="flex items-center justify-between">
                      <h1 className="text-lg font-semibold text-gray-700 capitalize flex-shrink-0">
                        {t('all')} {t(CONSTANTS.PRODUCT_TYPE.EVENT.value)}
                      </h1>
                      <Button
                        onClick={() => setShowCatalogFilterMobile(true)}
                        className="lg:hidden flex items-center justify-center w-24 h-7 py-1 px-4 gap-2 rounded-full border border-gray-300 bg-white hover:bg-primary text-gray-700 hover:text-white transition-colors"
                      >
                        <MdFilterList size={16} />
                        <span className="text-sm">Filter</span>
                      </Button>
                    </div>
                    <div className="w-full lg:max-w-md">
                      <SearchBar
                        value={query}
                        onChange={handleChangeQuery}
                        isSearching={isFetching && !isLoading}
                        onClear={() => {
                          handleChangeQuery('');
                        }}
                        className="search-bar-animation"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center mb-3 px-4 lg:px-0">
                    {/* Balon pencarian */}
                    {query && (
                      <div
                        key="search-tag"
                        className="flex items-center p-2 m-1 border border-gray-200 rounded-xl bg-white shadow-sm"
                      >
                        <button
                          type="button"
                          className="mr-2 text-gray-500 hover:text-gray-700 transition-colors"
                          onClick={() => {
                            handleChangeQuery('');
                          }}
                          aria-label="Hapus pencarian"
                        >
                          <MdClose className="w-4 h-4" />
                        </button>
                        <div className="text-sm flex items-center">
                          <span className="text-primary-600">{decodeURIComponent(query)}</span>
                        </div>
                      </div>
                    )}

                    {/* Balon kategori */}
                    {selectedCategories.length > 0 &&
                      selectedCategories.map((cat) => (
                        <div
                          key={cat.id}
                          className="flex items-center p-2 m-1 border border-gray-200 rounded-xl bg-white shadow-sm"
                        >
                          <button
                            type="button"
                            className="mr-2 text-gray-500 hover:text-gray-700 transition-colors"
                            onClick={() => handleRemoveFilterCategory(cat)}
                            aria-label={`Hapus kategori ${cat.title}`}
                          >
                            <MdClose className="w-4 h-4" />
                          </button>
                          <div className="text-sm flex items-center">
                            <span className="text-primary-600">{cat.title}</span>
                          </div>
                        </div>
                      ))}

                    {/* Tombol Hapus Semua - hanya muncul jika ada filter aktif */}
                    {(query || selectedCategories.length > 0) && (
                      <button
                        type="button"
                        onClick={handleResetFilter}
                        className="ml-2 mr-0 bg-transparent text-[#CD5035] border-none cursor-pointer font-semibold text-sm leading-4 font-primary"
                      >
                        Hapus Semua
                      </button>
                    )}
                  </div>
                  {/* Check if, after client-side filtering, there are any events to display */}
                  {processedAndFilteredEventPages.every(page => page.length === 0) ? (
                    <EmptyStateComponent
                      isFetching={isLoading || isFetching} // Show loading if initial load or refetching
                      onReset={handleResetFilter}
                      fullHeight
                      // Adjust empty message if filters might be causing no results
                      emptyMessage={
                        (priceMin || priceMax || query || categoryId || eventType || eventKind) && eventsData?.pages?.some(page => page.length > 0)
                          ? 'Tidak ada acara yang cocok dengan filter Anda. Coba sesuaikan filter atau lihat semua acara.'
                          : 'Tidak ada acara yang tersedia saat ini.'
                      }
                    />
                  ) : (
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 justify-items-center px-4 lg:px-0">
                      {processedAndFilteredEventPages.map((filteredEventsInPage, pageIndex) => (
                        <React.Fragment key={`page-${pageIndex}`}>
                          {filteredEventsInPage.map((event) => (
                            <ProductCard
                              key={event.uuid}
                              title={event.title}
                              image={event.image_url}
                              thumbnailVariations={event.thumbnail_variations}
                              price={event.ticket_price}
                              basePrice={event.sell_price}
                              earlyTicketPrice={event.early_ticket_price}
                              earlyBirdDate={event.early_ticket_date}
                              discount={event.discount}
                              slug={event.slug}
                              type={{ id: CONSTANTS.PRODUCT_TYPE.EVENT.id }}
                              location={event.location}
                              sessionFormatted={event.session_formatted}
                              instructor={event.organizer}
                              eventType={event.event_type}
                              eventKind={event.event_kind}
                              eventPackage={event.event_package}
                              searchQuery={query}
                              isPurchased={event.is_purchased}
                              showPrice
                              linkTo={`/event/${event.slug}`}
                              onClick={() => {
                                const gaPayload = {
                                  item_list_id: 'event_list',
                                  item_list_name: 'Event List',
                                  items: [
                                    {
                                      item_id: event.uuid,
                                      item_name: event.title,
                                      price: event.ticket_price,
                                    },
                                  ],
                                };
                                HELPERS.sendEventGA('select_item', gaPayload);
                              }}
                            />
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {hasNextPage && (
                    <div className="flex justify-center mt-8">
                      <Button
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        className="text-base mx-auto w-fit font-semibold transition-all duration-300 ease-in-out hover:bg-gray-100"
                        size={SIZES.MEDIUM}
                        variant={VARIANTS.GRAY_OUTLINE}
                      >
                        {isFetchingNextPage ? 'Memuat...' : 'Tampilkan Lebih Banyak'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </SmoothTransition>

            {/* Optionally show a subtle loading indicator when fetching but not initial load */}
            {isFetching && !isLoading && (
              <div
                className="fixed bottom-4 right-4 bg-white shadow-lg rounded-full px-4 py-2 text-sm text-gray-600 animate-pulse"
                role="status"
                aria-live="polite"
              >
                Memuat...
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EventCatalogue;
